<template>
  <div>
    <loading-panel v-if="(!account || loading) && !error"/>
    <card v-if="account" class="card">
      <template slot="header">
        <div class="d-flex">
          <div class="mr-auto p-2"><h4 class="card-title">{{ $t('accountCard.title') }}</h4></div>
          <create-account-deletion-btn
            v-if="(account.type === 'card' || account.type === 'mobile') && account.status == 'ok' && $auth.can({account_delete: 'create'}, 'permissions')"
            :card-account="account.type === 'card'" :account-id="account.id"/>
          <account-cap-limit-btn v-if="(account.type === 'card' || account.type === 'mobile') " :account-id="account.id" class="ml-2"/>
          <base-button type="neutral" size="sm" icon round class="ml-2 zi_100" @click="fullShow = !fullShow">
            <span
              :class="{'mdi_icon':true, 'mdi':true, 'mdi-18px':true, 'mdi-unfold-more-horizontal':!fullShow, 'mdi-unfold-less-horizontal':fullShow}"/>
          </base-button>
        </div>
      </template>
      <div class="row">
        <div class="col">
          <table class="striped_table w-100">
            <tbody v-if="fullShow">
            <tr>
              <td><label>{{ $t('accountCard.accountId') }}</label></td>
              <td class="pl-2">{{ account.id }}</td>
            </tr>
            <tr>
              <td><label>{{ $t('accountCard.language') }}</label></td>
              <td class="pl-2">{{ account.language }}</td>
            </tr>
            </tbody>
            <tbody>
            <tr>
              <td><label>{{ $t('accountCard.type') }}</label></td>
              <td class="pl-2 font-weight-bold">{{ account.type }}</td>
            </tr>
            <tr>
              <td><label>{{ $t('generic.status') }}</label></td>
              <td class="pl-2 font-weight-bold">
                <div class="row m-0 p-0">
                  <div class="col m-0 p-0 align-self-center"
                       :class="{ 'text-danger': account.status === 'suspended' }">
                    {{ account.status }}
                  </div>
                  <div class="col m-0 p-0 justify-content-end"
                       v-if="account.type == 'card' && $auth.can({account: 'update'}, 'permissions')">
                    <change-account-status-btn :account="account" class="float-right"/>
                  </div>
                </div>
              </td>
            </tr>
            <tr>
              <td><label>{{ $t('accountCard.trait') }}</label></td>
              <td class="pl-2 font-weight-bolder">
                <div class="row m-0 p-0">
                  <div class="col m-0 p-0 align-self-center">
                    {{ account.trait }}
                  </div>
                  <div class="col m-0 p-0 justify-content-end"
                       v-if="account.type == 'card' && $auth.can({account_trait: 'update'}, 'permissions')">
                    <change-account-trait-btn :account-id="account.id" class="float-right"/>
                  </div>
                </div>
              </td>
            </tr>
            <tr>
              <td><label>{{ $t('accountCard.balance') }}</label></td>
              <td class="pl-2 font-weight-bold">
                <div class="row m-0 p-0">
                  <div class="col m-0 p-0 align-self-center"
                       :class="{ 'text-danger': account.balance < 0 }">
                    {{ account.balance | money }}
                  </div>
                  <div class="col m-0 p-0"
                       v-if="(account.type === 'card' || account.type === 'mobile') && account.status != 'deleted' && $auth.can({account_money_claim: 'create'}, 'permissions')">
                    <create-money-claim-btn v-if="account.type !== 'emv_card'" :account-id="account.id"
                                            class="float-right"/>
                  </div>
                </div>
              </td>
            </tr>
            <tr>
              <td><label>{{ $t('accountCard.suspended_funds') }}</label></td>
              <td class="pl-2 font-weight-bold">
                <div class="row m-0 p-0">
                  <div class="col m-0 p-0 align-self-center" :class="{'text-danger' : suspendedFundsExists, }">
                    {{ account.suspended_funds | money }}
                  </div>
                </div>
              </td>
            </tr>
            <tr>
              <td><label>{{ $t('accountCard.discount') }}</label></td>
              <td class="pl-2 font-weight-bolder">
                <div class="row m-0 p-0">
                  <div class="col m-0 p-0 align-self-center">
                    {{ account.discount_id }}
                  </div>
                  <div class="col m-0 p-0 justify-content-end"
                       v-if="account.type == 'card' && $auth.can({account_discount: 'update'}, 'permissions')">
                    <change-account-discount-btn :account-id="account.id" class="float-right"/>
                  </div>
                </div>
              </td>
            </tr>
            <tr>
              <td><label>{{ $t('accountProfileDetails.emailAddress') }}</label></td>
              <td class="pl-2 word-wrap">{{ account.identity_details.email }}</td>
            </tr>
            <tr>
              <td><label>{{ $t('accountProfileDetails.referenceKey') }}</label></td>
              <td class="pl-2">{{ account.identity_details.reference_key }}</td>
            </tr>
            <tr>
              <td><label>{{ $t('accountProfileDetails.firstName') }}</label></td>
              <td class="pl-2">{{ account.personal_details.name }}</td>
            </tr>
            <tr>
              <td><label>{{ $t('accountProfileDetails.lastName') }}</label></td>
              <td class="pl-2">{{ account.personal_details.surname }}</td>
            </tr>
            <tr>
              <td><label>{{ $t('accountCard.municipality_id') }}</label></td>
              <td class="pl-2">
                <router-link v-if="account.municipality_id" :to="municipalityLink(account)">
                  {{ account.municipality_id }}
                </router-link>
                <span v-else>{{ account.municipality_id }}</span>
              </td>
            </tr>
            </tbody>
            <tbody v-if="fullShow">
            <tr>
              <td><label>{{ $t('accountProfileDetails.readOnly') }}</label></td>
              <td class="pl-2">{{ account.personal_details.read_only | trueFalse }}</td>
            </tr>
            <tr>
              <td><label>{{ $t('accountCard.last_mobile_app_login_at') }}</label></td>
              <td class="pl-2">{{ account.last_mobile_app_login_at | nullableTime }}</td>
            </tr>
            <tr>
              <td><label>{{ $t('accountCard.last_web_portal_login_at') }}</label></td>
              <td class="pl-2">{{ account.last_web_portal_login_at | nullableTime }}</td>
            </tr>
            <tr>
              <td><label>{{ $t('accountCard.mobile_app_device') }}</label></td>
              <td class="pl-2">{{ account.mobile_app_device }}</td>
            </tr>
            <tr>
              <td><label>{{ $t('accountCard.prefs') }}</label></td>
              <td class="pl-2">
                <meta-data-details :data="account.prefs"/>
              </td>
            </tr>
            <tr>
              <td><label>{{ $t('generic.created') }}</label></td>
              <td class="pl-2">{{ account.created_at | time }}</td>
            </tr>
            <tr>
              <td><label>{{ $t('generic.updated') }}</label></td>
              <td class="pl-2">{{ account.updated_at | time }}</td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
    </card>
  </div>
</template>
<script>
import CreateMoneyClaimBtn from "./CreateMoneyClaimBtn";
import CreateAccountDeletionBtn from "./CreateAccountDeletionBtn";
import EditMoneyClaim from "./AccountMoneyClaim";
import {mapActions, mapState} from "vuex";
import LoadingPanel from "src/components/LoadingPanel";
import ChangeAccountDiscountBtn from "@/pages/Account/AccountDetails/ChangeAccountDiscountBtn";
import ChangeAccountStatusBtn from "@/pages/Account/AccountDetails/ChangeAccountStatusBtn";
import ChangeAccountTraitBtn from "@/pages/Account/AccountDetails/ChangeAccountTraitBtn";
import AccountCapLimitBtn from "@/pages/Account/AccountDetails/AccountCapLimitBtn";
import MetaDataDetails from "src/components/MetaData/MetaDataDetails";
import linkUtils from "@/util/link_utils";

export default {
  name: "AccountDetails",
  components: {
    ChangeAccountDiscountBtn,
    ChangeAccountStatusBtn,
    ChangeAccountTraitBtn,
    CreateMoneyClaimBtn,
    EditMoneyClaim,
    CreateAccountDeletionBtn,
    AccountCapLimitBtn,
    LoadingPanel,
    MetaDataDetails
  },
  data() {
    return {
      fullShow: false,
      currentAccountId: "",
    };
  },
  computed: {
    ...mapState({
      loading: state => state.account.loading,
      error: state => state.account.error,
      account: state => state.account.data,
    }),
    suspendedFundsExists() {
      if (this.account && this.account.suspended_funds != 0) {
        return true
      }
      return false
    }
  },
  methods: {
    ...mapActions(['LoadAccount']),
    municipalityLink(details) {
      return linkUtils.municipalityPath(this.$router, {id: details.municipality_id});
    },
  },
  mounted() {
    this.currentAccountId = this.$route.params.id;
    this.LoadAccount(this.$route.params.id);
  },
  watch: {
    $route() {
      if (this.currentAccountId !== this.$route.params.id) {
        this.currentAccountId = this.$route.params.id;
        this.LoadAccount(this.$route.params.id)
      }
    }
  }
};
</script>
<style>
</style>
